import { Dispatch, SetStateAction, useCallback } from 'react';
import Text from 'common/ui/Text';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Close from '@mui/icons-material/Close';
import { useMutation } from '@apollo/client';
import { Stack, Spacer } from 'common/ui/Layout';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { SettingsCard, SettingsCardContent, SettingsCardHeader } from './SettingsLayoutComponents';
import { GET_ME, UPDATE_WORKLIST_SETTINGS } from 'modules/Apollo/queries';
import assocPath from 'ramda/es/assocPath';
import { telemetryContextFor } from 'modules/Apollo/types';

type WorklistSettingsTabProps = {
  onClose: () => void;
  setError: Dispatch<SetStateAction<string | null>>;
};

export const WorklistSettingsTab = ({
  onClose,
  setError,
}: WorklistSettingsTabProps): React.ReactElement => {
  const [updateWorklistSettings] = useMutation(UPDATE_WORKLIST_SETTINGS, {
    context: APOLLO_QUERY_CONTEXT,
  });
  const { data } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const worklistSettings = data?.me.worklistSettings;

  const openPatientJacketOnRead = worklistSettings?.openPatientJacketOnRead ?? true;

  const toggleOpenPatientJacketOnRead = useCallback(
    async (_evt: React.ChangeEvent<HTMLInputElement>) => {
      const updatedResponse = {
        ...worklistSettings,
        openPatientJacketOnRead: !openPatientJacketOnRead,
      } as const;

      await updateWorklistSettings({
        variables: { openPatientJacketOnRead: !openPatientJacketOnRead },
        optimisticResponse: {
          __typename: 'Mutation',
          updateWorklistSettings: {
            __typename: 'WorklistSettings',
            ...updatedResponse,
          },
        },
        update: (proxy) => {
          proxy.writeQuery({
            query: GET_ME,
            data: assocPath(
              ['me', 'worklistSettings', 'openPatientJacketOnRead'],
              !openPatientJacketOnRead,
              data
            ),
          });
        },
      });
    },
    [data, openPatientJacketOnRead, updateWorklistSettings, worklistSettings]
  );

  return (
    <SettingsCard>
      <SettingsCardHeader>
        <Stack alignY="center">
          <Text variant="display2">Worklist Settings</Text>
          <Spacer />
          <IconButton onClick={onClose} size="large">
            <Close />
          </IconButton>
        </Stack>
      </SettingsCardHeader>
      <SettingsCardContent>
        <Stack vertical space="small">
          <Text variant="display1" display="block">
            General
          </Text>
          <Stack alignY="center" alignX="between">
            <Text>Automatically open patient jacket when opening an exam</Text>
            <Switch
              data-testid="open-patient-jacket-switch"
              checked={openPatientJacketOnRead}
              onChange={toggleOpenPatientJacketOnRead}
            />
          </Stack>
        </Stack>
      </SettingsCardContent>
    </SettingsCard>
  );
};

WorklistSettingsTab.displayName = 'WorklistSettingsTab';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(WorklistSettingsTab);
